import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Activity, ExternalLink } from 'lucide-react';
import { springConfig, fadeInUp } from '../utils/animations';
import { FUNCTION_HEALTH_AFFILIATE_LINK } from '../utils/constants';

interface FunctionHealthCTAProps {
  className?: string;
}

const FunctionHealthCTA: React.FC<FunctionHealthCTAProps> = ({ className = '' }) => {
  const prefersReducedMotion = useReducedMotion();

  // Button hover animation variants
  const buttonVariants = {
    rest: { scale: 1 },
    hover: {
      scale: 1.03,
      transition: springConfig.snappy
    },
    tap: {
      scale: 0.97,
      transition: { duration: 0.1 }
    }
  };

  // Icon animation variants
  const iconVariants = {
    rest: { rotate: 0 },
    hover: {
      rotate: [0, -10, 10, 0],
      transition: { duration: 0.4 }
    }
  };

  // Pulse animation for the activity icon background
  const pulseVariants = {
    animate: {
      scale: [1, 1.1, 1],
      opacity: [0.5, 0.3, 0.5],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut' as const
      }
    }
  };

  const handleClick = () => {
    window.open(FUNCTION_HEALTH_AFFILIATE_LINK, '_blank', 'noopener,noreferrer');
  };

  // Reduced motion version
  if (prefersReducedMotion) {
    return (
      <div className={`mt-10 ${className}`}>
        <div className="bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-catppuccin-surface1 dark:to-catppuccin-surface2 rounded-xl p-6 border border-teal-100 dark:border-catppuccin-overlay0 shadow-sm">
          <div className="flex flex-col items-center text-center">
            {/* Icon */}
            <div className="relative mb-4">
              <div className="w-14 h-14 rounded-full bg-teal-100 dark:bg-catppuccin-teal/20 flex items-center justify-center">
                <Activity size={28} className="text-teal-600 dark:text-catppuccin-teal" />
              </div>
            </div>

            {/* Headline */}
            <h3 className="text-xl font-bold text-teal-800 dark:text-catppuccin-teal mb-2">
              Know Your Real Numbers
            </h3>

            {/* Value proposition */}
            <p className="text-gray-600 dark:text-catppuccin-subtext0 mb-5 max-w-md">
              Go beyond the heartbeat count. Get 100+ lab tests twice a year to understand what's really happening inside your body.
            </p>

            {/* CTA Button */}
            <button
              onClick={handleClick}
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 dark:bg-catppuccin-teal text-white dark:text-catppuccin-base font-semibold rounded-full hover:bg-teal-700 dark:hover:bg-catppuccin-green transition-colors duration-200"
            >
              <span>Explore Function Health</span>
              <ExternalLink size={18} />
            </button>

            {/* Disclaimer */}
            <p className="mt-4 text-xs text-gray-500 dark:text-catppuccin-overlay1">
              Partner link - we may earn a commission at no extra cost to you
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Animated version
  return (
    <motion.div
      className={`mt-10 ${className}`}
      variants={fadeInUp}
      initial="hidden"
      animate="visible"
    >
      <motion.div
        className="bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-catppuccin-surface1 dark:to-catppuccin-surface2 rounded-xl p-6 border border-teal-100 dark:border-catppuccin-overlay0 shadow-sm"
        whileHover={{ y: -2, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
        transition={springConfig.gentle}
      >
        <div className="flex flex-col items-center text-center">
          {/* Icon with pulse effect */}
          <div className="relative mb-4">
            <motion.div
              className="absolute inset-0 w-14 h-14 rounded-full bg-teal-200 dark:bg-catppuccin-teal/30"
              variants={pulseVariants}
              animate="animate"
            />
            <div className="relative w-14 h-14 rounded-full bg-teal-100 dark:bg-catppuccin-teal/20 flex items-center justify-center">
              <Activity size={28} className="text-teal-600 dark:text-catppuccin-teal" />
            </div>
          </div>

          {/* Headline */}
          <motion.h3
            className="text-xl font-bold text-teal-800 dark:text-catppuccin-teal mb-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, ...springConfig.gentle }}
          >
            Know Your Real Numbers
          </motion.h3>

          {/* Value proposition */}
          <motion.p
            className="text-gray-600 dark:text-catppuccin-subtext0 mb-5 max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Go beyond the heartbeat count. Get 100+ lab tests twice a year to understand what's really happening inside your body.
          </motion.p>

          {/* CTA Button */}
          <motion.button
            onClick={handleClick}
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 dark:bg-catppuccin-teal text-white dark:text-catppuccin-base font-semibold rounded-full hover:bg-teal-700 dark:hover:bg-catppuccin-green transition-colors duration-200"
            variants={buttonVariants}
            initial="rest"
            whileHover="hover"
            whileTap="tap"
          >
            <motion.span variants={iconVariants}>
              <Activity size={18} />
            </motion.span>
            <span>Explore Function Health</span>
            <ExternalLink size={18} />
          </motion.button>

          {/* Disclaimer */}
          <motion.p
            className="mt-4 text-xs text-gray-500 dark:text-catppuccin-overlay1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Partner link - we may earn a commission at no extra cost to you
          </motion.p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FunctionHealthCTA;
